/**
 * Пул событий года, v1: 30 событий как данные.
 *
 * Вес каждого события — функция от показателей страны (`ctx.ds`), возраста
 * и состояния жизни; 0 означает «недоступно». Веса записаны в масштабе
 * ПРИМЕРНОЙ ГОДОВОЙ ВЕРОЯТНОСТИ: розыгрыш идёт как
 * P(событие есть) = EVENT_CHANCE (0.6), затем weighted по пулу, поэтому
 * фактическая вероятность события = 0.6 · w / Σw. Σw у типичной жизни
 * 0.4–1.0 (ребёнок в Японии ≈ 0.4, взрослый в Германии ≈ 0.65, в Нигерии
 * ≈ 1.0), поэтому число в таблице читается как порядок величины
 * («layoff ≈ 2–4 % в год»), а не как точная вероятность.
 * Значения — игровая калибровка, не статистика; статистика входит через
 * показатели страны (fertility, urban, smoking, homicide, childMarriage,
 * agriShare, gdpPpp) и доли травм/ДТП региона из CAUSE_SHARES.
 *
 * `apply(life, rng)` возвращает новую жизнь: состояния клампятся в
 * [0, 100], деньги и долг — конечные и ≥ 0. Тяжёлые события (`heavy`) —
 * алкоголь, азарт, насилие — не описывают способ или сцену; при
 * `flags.skipHeavy` запись получает `event.neutral` (см. index.ts).
 * Все случайности — из переданного `Rng`; вход не мутируется.
 */
import { CAUSE_SHARES, getCountry, getValue, type Dataset } from '@life/data';
import { ageBand } from '../death';
import { jobTitleKey, LEVEL_MULT, MAX_JOB_LEVEL, type JobLevel } from '../economy';
import { clampStats } from '../focus';
import type { Rng } from '../rng';
import type { Life, Stats } from '../types';

export interface EventCtx {
  ds: Dataset;
  life: Life;
  /** Календарный год = birthYear + age. */
  year: number;
}

export interface EventDef {
  id: string;
  /** Тяжёлая тема: при flags.skipHeavy текст заменяется event.neutral. */
  heavy?: boolean;
  /** Вес в пуле; 0 = недоступно. */
  weight: (c: EventCtx) => number;
  apply: (life: Life, rng: Rng) => Life;
  /** Ключ текста в каталоге i18n: `event.<id>`. */
  textKey: string;
}

/* ---------- флаги жизни, которые ведут события ---------- */

/** Первая любовь уже была. */
export const FLAG_LOVE_FIRST = 'loveFirst';
/** Сколько родителей умерло (0..2). */
export const FLAG_PARENTS_DEAD = 'parentsDead';
/** Год увольнения. */
export const FLAG_LAYOFF_YEAR = 'layoffYear';
/** Первая поездка уже была. */
export const FLAG_TRAVELED = 'traveled';
/** Календарный год последнего несчастного случая — ведёт решение health.treatment. */
export const FLAG_ACCIDENT_YEAR = 'accidentYear';
/** Болезнь родителя уже была (одна запись на жизнь, иначе журнал засоряется). */
export const FLAG_PARENT_ILL = 'parentIll';
/** Домашнее насилие уже было: тяжёлое событие один раз на жизнь. */
export const FLAG_DOMESTIC_VIOLENCE = 'domesticViolence';
/** Водительские права (мини-игра F5): риск `health.accident` × LICENSE_ACCIDENT_MULT, +1 уровень при найме. */
export const FLAG_LICENSE = 'license';
/** Множитель веса несчастного случая у водителя (interactives.md F5). */
export const LICENSE_ACCIDENT_MULT = 1.3;
/**
 * Порядок предметов «что интереснее» из мини-игры G2 «урок»: строка вида
 * `sport,math,lang,art`. Первый предмет взвешивает школьные события:
 * спорт — буллинг реже, математика — олимпиада чаще.
 */
export const FLAG_LESSON_ORDER = 'lessonOrder';
export const LESSON_SPORT_BULLYING_MULT = 0.7;
export const LESSON_MATH_AWARD_MULT = 1.3;

/* ---------- константы эффектов ---------- */

const PARENTS = 2;
const SCHOOL_MIN_AGE = 6;
const SCHOOL_MAX_AGE = 16;
const BULLYING_MAX_RELATIONS = 40;
const AWARD_MIN_KNOWLEDGE = 70;
const AWARD_MAX_AGE = 22;
const SIBLING_MAX_AGE = 11;
/** Подростковый возраст: с него возможны потеря родителя, алкоголь, домашнее насилие. */
const TEEN_AGE = 15;
const LOVE_MIN_AGE = 15;
const LOVE_MAX_AGE = 25;
const ADULT_AGE = 18;
const CHILD_MAX_AGE = 45;
const SMOKING_MIN_AGE = 14;
const SMOKING_MAX_AGE = 25;
const GRANDCHILD_MIN_AGE = 55;
const GRADUATE_MIN_AGE = 40;
const GRADUATE_MAX_AGE = 60;
const PARENT_DIED_RAMP_AGE = 30;
const PARENT_DIED_MAX_WEIGHT = 0.12;
/** Базовая вероятность увольнения («3 % базово»). */
export const LAYOFF_BASE = 0.03;
const SCAM_SHARE = 0.3;
/**
 * Тяжёлые события калибруются по доле жизней (этап 1): после каждого
 * пишется `help.lines`, а их должно быть ≤ 0.5 на жизнь. Фактическая
 * годовая вероятность ≈ 0.6·w/Σw, а Σw взрослого ≈ 0.3, то есть ≈ 2w.
 * Итог на N=3000: алкоголь ≈ 12 % жизней, азарт ≈ 5 %, нападение ≈ 9 %,
 * домашнее насилие ≈ 20 % женщин и ≈ 9 % мужчин (один раз на жизнь).
 */
const ALCOHOL_BASE = 0.0013;
const ALCOHOL_UNHAPPY = 0.003;
const GAMBLING_BASE = 0.0006;
const GAMBLING_UNHAPPY = 0.0012;
const VICTIM_PER_HOMICIDE = 0.00025;
const DV_BASE = 0.0005;
const DV_CHILD_MARRIAGE = 0.005;
const DV_POOR_MULT = 2;
const DV_WOMAN_MULT = 2.2;
const UNHAPPY = 40;
const GAMBLING_SHARE = 0.4;
const DISASTER_SHARE = 0.2;
/** Нежданные деньги: константа ППС плюс половина годового дохода. */
const WINDFALL_BASE = 500;
const WINDFALL_INCOME_SHARE = 0.5;
/** Первая поездка доступна при доходе выше стольких ВВП на душу. */
const TRAVEL_INCOME_TO_GDP = 2;
const LOW_DECILE = 3;

/* ---------- помощники ---------- */

function clampMoney(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0;
}

function nonNeg(w: number): number {
  return Number.isFinite(w) && w > 0 ? w : 0;
}

/** Сдвиг состояний с клампом; остальные поля жизни — через overrides. */
function shift(life: Life, delta: Partial<Stats>, overrides: Partial<Life> = {}): Life {
  const stats = clampStats({
    health: life.stats.health + (delta.health ?? 0),
    happiness: life.stats.happiness + (delta.happiness ?? 0),
    knowledge: life.stats.knowledge + (delta.knowledge ?? 0),
    relations: life.stats.relations + (delta.relations ?? 0),
  });
  return { ...life, ...overrides, stats };
}

function flag(life: Life, key: string, value: number | boolean): Life['flags'] {
  return { ...life.flags, [key]: value };
}

function ind(c: EventCtx, name: Parameters<typeof getValue>[2]): number {
  return nonNeg(getValue(c.ds, c.life.iso3, name, c.year).v);
}

const inAge = (life: Life, lo: number, hi: number): boolean => life.age >= lo && life.age <= hi;
/** Первый предмет из `flags.lessonOrder` (G2) или null. */
const firstLesson = (life: Life): string | null => {
  const raw = life.flags[FLAG_LESSON_ORDER];
  return typeof raw === 'string' && raw.length > 0 ? (raw.split(',')[0] ?? null) : null;
};
const hasJob = (life: Life): boolean => life.job !== null;
const parentsAlive = (life: Life): number => PARENTS - Math.min(PARENTS, Number(life.flags[FLAG_PARENTS_DEAD] ?? 0));

/** Доля травм и ДТП среди смертей региона в возрастной полосе жизни. */
function injuryShare(c: EventCtx): number {
  const region = getCountry(c.ds, c.life.iso3).whoRegion;
  const shares = CAUSE_SHARES[region][ageBand(c.life.age)];
  return nonNeg((shares.injury ?? 0) + (shares.traffic ?? 0));
}

/* ---------- таблица ---------- */

export const EVENT_DEFS: readonly EventDef[] = [
  // Друзья.
  {
    id: 'friend.new',
    textKey: 'event.friend.new',
    weight: ({ life }) => (life.age >= 3 ? 0.08 : 0),
    apply: (life) => shift(life, { relations: 5, happiness: 3 }),
  },
  {
    id: 'friend.lost',
    textKey: 'event.friend.lost',
    weight: ({ life }) => (life.age >= 5 && life.stats.relations > 20 ? 0.05 : 0),
    apply: (life) => shift(life, { relations: -5, happiness: -4 }),
  },

  // Школа.
  {
    id: 'school.bullying',
    textKey: 'event.school.bullying',
    weight: ({ life }) =>
      inAge(life, SCHOOL_MIN_AGE, SCHOOL_MAX_AGE) && life.stats.relations < BULLYING_MAX_RELATIONS
        ? 0.1 * (firstLesson(life) === 'sport' ? LESSON_SPORT_BULLYING_MULT : 1)
        : 0,
    apply: (life) => shift(life, { happiness: -8, relations: -3 }),
  },
  {
    id: 'school.award',
    textKey: 'event.school.award',
    weight: ({ life }) =>
      inAge(life, SCHOOL_MIN_AGE, AWARD_MAX_AGE) && life.stats.knowledge > AWARD_MIN_KNOWLEDGE
        ? 0.06 * (firstLesson(life) === 'math' ? LESSON_MATH_AWARD_MULT : 1)
        : 0,
    apply: (life) => shift(life, { knowledge: 3, happiness: 5 }),
  },

  // Семья.
  {
    id: 'family.newSibling',
    textKey: 'event.family.newSibling',
    weight: (c) => (c.life.age <= SIBLING_MAX_AGE ? 0.04 * ind(c, 'fertility') : 0),
    apply: (life) => shift(life, { relations: 2, happiness: 2 }, { siblings: life.siblings + 1 }),
  },
  {
    id: 'family.parentIll',
    textKey: 'event.family.parentIll',
    weight: ({ life }) => (inAge(life, 10, 60) && parentsAlive(life) > 0 && !life.flags[FLAG_PARENT_ILL] ? 0.04 : 0),
    apply: (life) => shift(life, { happiness: -5, relations: 2 }, { flags: flag(life, FLAG_PARENT_ILL, true) }),
  },
  {
    id: 'family.parentDied',
    textKey: 'event.family.parentDied',
    weight: ({ life }) => {
      // Одна запись о потере родителя на жизнь (журнал); второго родителя жизнь хоронит молча.
      if (life.age < TEEN_AGE || parentsAlive(life) < PARENTS) return 0;
      const ramp = Math.max(0, life.age - PARENT_DIED_RAMP_AGE);
      return Math.min(PARENT_DIED_MAX_WEIGHT, 0.01 + ramp * 0.004);
    },
    apply: (life) =>
      shift(life, { happiness: -10, relations: -3 }, {
        parentsAlive: Math.max(0, life.parentsAlive - 1),
        flags: flag(life, FLAG_PARENTS_DEAD, PARENTS - parentsAlive(life) + 1),
      }),
  },
  {
    id: 'family.moveCity',
    textKey: 'event.family.moveCity',
    weight: (c) => (!c.life.urban && inAge(c.life, 5, 40) ? 0.04 * (ind(c, 'urban') / 100) : 0),
    apply: (life) => shift(life, { relations: -3, happiness: 1, knowledge: 2 }, { urban: true }),
  },

  // Любовь.
  {
    id: 'love.first',
    textKey: 'event.love.first',
    weight: ({ life }) => (inAge(life, LOVE_MIN_AGE, LOVE_MAX_AGE) && !life.partner && !life.flags[FLAG_LOVE_FIRST] ? 0.15 : 0),
    apply: (life) => shift(life, { happiness: 8, relations: 4 }, { flags: flag(life, FLAG_LOVE_FIRST, true) }),
  },
  {
    id: 'love.breakup',
    textKey: 'event.love.breakup',
    weight: ({ life }) => (life.partner ? 0.03 + (life.stats.relations < 40 ? 0.03 : 0) : 0),
    apply: (life) => shift(life, { happiness: -10, relations: -5 }, { partner: false }),
  },

  // Работа.
  {
    id: 'work.promotion',
    textKey: 'event.work.promotion',
    weight: ({ life }) => (life.job && life.job.level < MAX_JOB_LEVEL ? 0.03 + life.stats.knowledge / 2000 : 0),
    apply: (life) => {
      if (!life.job || life.job.level >= MAX_JOB_LEVEL) return shift(life, { happiness: 5 });
      const level = (life.job.level + 1) as JobLevel;
      const income = clampMoney((life.job.income * LEVEL_MULT[level]) / LEVEL_MULT[life.job.level]);
      return shift(life, { happiness: 5 }, { job: { titleKey: jobTitleKey(level), level, income } });
    },
  },
  {
    id: 'work.layoff',
    textKey: 'event.work.layoff',
    weight: ({ life }) => (hasJob(life) ? LAYOFF_BASE : 0),
    apply: (life) =>
      shift(life, { happiness: -8 }, { job: null, flags: flag(life, FLAG_LAYOFF_YEAR, life.birthYear + life.age) }),
  },
  {
    id: 'work.injury',
    textKey: 'event.work.injury',
    weight: (c) => (hasJob(c.life) ? 0.01 + 0.05 * (ind(c, 'agriShare') / 100) : 0),
    apply: (life) => shift(life, { health: -10, happiness: -3 }),
  },

  // Деньги.
  {
    id: 'money.windfall',
    textKey: 'event.money.windfall',
    weight: ({ life }) => (life.age >= 16 ? 0.02 : 0),
    apply: (life) =>
      shift(life, { happiness: 5 }, { money: clampMoney(life.money + WINDFALL_BASE + WINDFALL_INCOME_SHARE * (life.job?.income ?? 0)) }),
  },
  {
    id: 'money.scam',
    textKey: 'event.money.scam',
    weight: ({ life }) => (life.age >= 16 && life.money > 0 ? 0.02 : 0),
    apply: (life) => shift(life, { happiness: -5 }, { money: clampMoney(life.money * (1 - SCAM_SHARE)) }),
  },

  // Здоровье.
  {
    id: 'health.accident',
    textKey: 'event.health.accident',
    weight: (c) => (c.life.age >= 1 ? (0.01 + 0.2 * injuryShare(c)) * (c.life.flags[FLAG_LICENSE] === true ? LICENSE_ACCIDENT_MULT : 1) : 0),
    apply: (life) =>
      shift(life, { health: -12, happiness: -3 }, { flags: flag(life, FLAG_ACCIDENT_YEAR, life.birthYear + life.age) }),
  },
  {
    id: 'health.recovery',
    textKey: 'event.health.recovery',
    weight: ({ life }) => (life.conditions.length > 0 ? 0.05 : 0),
    apply: (life) => shift(life, { health: 8, happiness: 4 }),
  },

  // Привычки.
  {
    id: 'habit.smokingStart',
    textKey: 'event.habit.smokingStart',
    weight: (c) => (inAge(c.life, SMOKING_MIN_AGE, SMOKING_MAX_AGE) && !c.life.habits.smoking ? 0.4 * (ind(c, 'smoking') / 100) : 0),
    apply: (life) => shift(life, { happiness: 1 }, { habits: { ...life.habits, smoking: true } }),
  },
  {
    id: 'habit.smokingQuit',
    textKey: 'event.habit.smokingQuit',
    weight: ({ life }) => (life.habits.smoking ? 0.04 : 0),
    apply: (life) => shift(life, { health: 3, happiness: 2 }, { habits: { ...life.habits, smoking: false } }),
  },
  {
    id: 'habit.alcohol',
    heavy: true,
    textKey: 'event.habit.alcohol',
    weight: ({ life }) =>
      life.age >= TEEN_AGE && !life.habits.alcohol ? ALCOHOL_BASE + (life.stats.happiness < UNHAPPY ? ALCOHOL_UNHAPPY : 0) : 0,
    apply: (life) => shift(life, { health: -3, relations: -3 }, { habits: { ...life.habits, alcohol: true } }),
  },
  {
    id: 'habit.gambling',
    heavy: true,
    textKey: 'event.habit.gambling',
    weight: ({ life }) =>
      life.age >= ADULT_AGE && !life.habits.gambling ? GAMBLING_BASE + (life.stats.happiness < UNHAPPY ? GAMBLING_UNHAPPY : 0) : 0,
    apply: (life) =>
      shift(life, { happiness: -3 }, { habits: { ...life.habits, gambling: true }, money: clampMoney(life.money * (1 - GAMBLING_SHARE)) }),
  },

  // Преступность: тяжёлые темы, без описания сцены.
  {
    id: 'crime.victim',
    heavy: true,
    textKey: 'event.crime.victim',
    weight: (c) => (c.life.age >= 5 ? VICTIM_PER_HOMICIDE * ind(c, 'homicide') : 0),
    apply: (life) => shift(life, { health: -8, happiness: -8 }),
  },
  {
    id: 'crime.domesticViolence',
    heavy: true,
    textKey: 'event.crime.domesticViolence',
    weight: (c) => {
      if (c.life.age < TEEN_AGE || c.life.flags[FLAG_DOMESTIC_VIOLENCE]) return 0;
      // childMarriage в датасете в процентах (IND 22.3) — в формулу входит доля.
      const base = DV_BASE + DV_CHILD_MARRIAGE * (ind(c, 'childMarriage') / 100);
      const decile = c.life.familyDecile <= LOW_DECILE ? DV_POOR_MULT : 1;
      const sex = c.life.sex === 'f' ? DV_WOMAN_MULT : 1;
      return base * decile * sex;
    },
    apply: (life) => shift(life, { health: -5, happiness: -10, relations: -5 }, { flags: flag(life, FLAG_DOMESTIC_VIOLENCE, true) }),
  },

  // Община.
  {
    id: 'community.festival',
    textKey: 'event.community.festival',
    weight: () => 0.05,
    apply: (life) => shift(life, { happiness: 4, relations: 2 }),
  },
  {
    id: 'community.disaster',
    textKey: 'event.community.disaster',
    weight: () => 0.01,
    apply: (life) => shift(life, { health: -5, happiness: -6 }, { money: clampMoney(life.money * (1 - DISASTER_SHARE)) }),
  },
  {
    id: 'faith.community',
    textKey: 'event.faith.community',
    weight: ({ life }) => (life.age >= 12 ? 0.03 + (life.focus === 'faith' ? 0.03 : 0) : 0),
    apply: (life) => shift(life, { relations: 4, happiness: 3 }),
  },

  // Путешествие.
  {
    id: 'travel.first',
    textKey: 'event.travel.first',
    weight: (c) => {
      const { life } = c;
      if (life.age < 16 || life.flags[FLAG_TRAVELED] || !life.job) return 0;
      return life.job.income > TRAVEL_INCOME_TO_GDP * ind(c, 'gdpPpp') ? 0.1 : 0;
    },
    apply: (life) => shift(life, { knowledge: 3, happiness: 6 }, { flags: flag(life, FLAG_TRAVELED, true) }),
  },

  // Дети и внуки.
  {
    id: 'child.born',
    textKey: 'event.child.born',
    // Один источник рождений с решением family.child: оба упираются в round(fertility) страны,
    // иначе дети удваивались (4.9 на жизнь при мировом TFR ≈ 2.3) и расходы вели к банкротствам.
    weight: (c) => {
      const { life } = c;
      if (!life.partner || !inAge(life, ADULT_AGE, CHILD_MAX_AGE)) return 0;
      const fertility = ind(c, 'fertility');
      return life.children < Math.round(fertility) ? 0.08 * fertility : 0;
    },
    apply: (life) => shift(life, { happiness: 6, relations: 3 }, { children: life.children + 1, childrenAges: [...life.childrenAges, 0] }),
  },
  {
    id: 'child.graduated',
    textKey: 'event.child.graduated',
    weight: ({ life }) => (life.children > 0 && inAge(life, GRADUATE_MIN_AGE, GRADUATE_MAX_AGE) ? 0.04 * life.children : 0),
    apply: (life) => shift(life, { happiness: 5, relations: 2 }),
  },
  {
    id: 'old.grandchild',
    textKey: 'event.old.grandchild',
    weight: ({ life }) => (life.children > 0 && life.age >= GRANDCHILD_MIN_AGE ? 0.05 * life.children : 0),
    apply: (life) => shift(life, { happiness: 6, relations: 4 }),
  },
];
